"use client"

import { useState, useEffect, useCallback } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"
import { Mobile, PC, Tablet, Notebook } from "../../ReactiveMode"
import EmptyState from "../../../components/common/EmptyState"
import BoardLoading from "../../../components/common/BoardLoading"
import actionTypes from "../../../constants/actionTypes"
import "../../../resources/scss/church-news.scss"

const ChurchNews = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [searchTerm, setSearchTerm] = useState("")
  const [currentPage, setCurrentPage] = useState(1)
  const [isSearching, setIsSearching] = useState(false)
  const [showDropdown, setShowDropdown] = useState({})
  const newsPerPage = 10

  // BOARD_ID를 5로 설정
  const BOARD_ID = 5

  // Redux state에서 데이터 가져오기
  const boardState = useSelector((state) => state.board.boards[BOARD_ID]) || {
    loading: false,
    error: null,
    data: {
      content: [],
      totalPages: 0,
      totalElements: 0,
      number: 0,
      size: 10,
    },
  }

  // 로그인 정보 가져오기 (role 정보 포함)
  const loginData = useSelector((state) => state.login)

  const { loading, error, data } = boardState
  const { content: news = [], totalPages = 0, totalElements = 0 } = data

  // 디바운싱을 위한 타이머
  const [searchTimer, setSearchTimer] = useState(null)

  // 관리자 권한 체크 함수
  const hasAdminPermission = () => {
    const { isLogin, role } = loginData

    if (!isLogin || !role) {
      return false
    }

    const adminRoles = ["ROLE_WEB_ADMIN", "ROLE_ADMIN"]
    const hasPermission = adminRoles.includes(role)

    return hasPermission
  }

  // 데이터 로드 함수
  const loadNews = useCallback(
      (page = 1, search = "") => {

        setIsSearching(!!search)

        dispatch({
          type: actionTypes.BOARD_FETCH,
          payload: {
            menuId: BOARD_ID,
            page: page - 1,
            size: newsPerPage,
            search: search || undefined,
          },
        })
      },
      [dispatch, BOARD_ID, newsPerPage],
  )

  // 컴포넌트 마운트 시 데이터 로드
  useEffect(() => {
    loadNews(1, searchTerm)
  }, [])

  // 검색어 변경 처리 (디바운싱 적용)
  const handleSearchChange = (value) => {
    setSearchTerm(value)

    // 기존 타이머 클리어
    if (searchTimer) {
      clearTimeout(searchTimer)
    }

    // 새 타이머 설정 (500ms 후 검색 실행)
    const newTimer = setTimeout(() => {
      setCurrentPage(1)
      loadNews(1, value)
    }, 500)

    setSearchTimer(newTimer)
  }

  // 검색 실행
  const handleSearch = () => {
    setCurrentPage(1)
    loadNews(1, searchTerm)
  }

  // 검색 초기화
  const handleClearSearch = () => {
    setSearchTerm("")
    setCurrentPage(1)
    setIsSearching(false)
    loadNews(1, "")
  }

  // 페이지네이션 처리
  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber)
    loadNews(pageNumber, searchTerm)
  }

  // 글쓰기 버튼 클릭 (권한 체크 추가)
  const handleWriteClick = () => {
    if (!hasAdminPermission()) {
      alert("글쓰기 권한이 없습니다. 관리자만 글을 작성할 수 있습니다.")
      return
    }
    navigate(`/church-news/${BOARD_ID}/write`)
  }

  // 게시물 ID 추출 함수 - boardId 사용
  const getPostId = (item) => {
    // Redux DevTools에서 확인한 바로는 boardId가 실제 게시물 ID인 것 같음
    return item.boardId || item.id || item.postId || item.contentId || item.seq || item.no
  }

  // 게시물 번호 표시 함수
  const getDisplayNumber = (item, index) => {
    const postId = getPostId(item)
    if (postId) {
      return postId
    }
    // ID가 없으면 계산된 번호 사용
    return totalElements - (currentPage - 1) * newsPerPage - index
  }

  // 게시글 클릭 처리
  const handlePostClick = (item, index) => {
    // boardId를 게시물 ID로 사용
    const postId = item.boardId

    // 게시물 ID 유효성 검사
    if (!postId) {
      console.error("게시물 ID를 찾을 수 없습니다:", item)
      alert("게시물 정보를 찾을 수 없습니다.")
      return
    }

    // boardId를 게시물 ID로 사용하여 상세페이지 이동
    const finalUrl = `/church-news/${BOARD_ID}/${postId}`

    try {
      navigate(finalUrl)
    } catch (error) {
      console.error("페이지 이동 오류:", error)
      alert("페이지 이동 중 오류가 발생했습니다.")
    }
  }

  // 드롭다운 토글
  const toggleDropdown = (postId) => {
    setShowDropdown((prev) => ({
      ...prev,
      [postId]: !prev[postId],
    }))
  }

  // 수정 버튼 클릭
  const handleEditClick = (item, e) => {
    e.stopPropagation()
    const postId = getPostId(item)
    if (!postId) {
      alert("게시물 정보를 찾을 수 없습니다.")
      return
    }
    navigate(`/church-news/${BOARD_ID}/edit/${postId}`)
    setShowDropdown({})
  }

  // 삭제 버튼 클릭
  const handleDeleteClick = (item, e) => {
    e.stopPropagation()
    const postId = getPostId(item)
    if (!postId) {
      alert("게시물 정보를 찾을 수 없습니다.")
      return
    }

    if (window.confirm(`"${item.title}" 게시글을 삭제하시겠습니까?`)) {
      dispatch({
        type: actionTypes.BOARD_DELETE,
        payload: {
          id: postId,
          menuId: BOARD_ID,
        },
      })
      setShowDropdown({})
      // 삭제 후 목록 새로고침
      setTimeout(() => {
        loadNews(currentPage, searchTerm)
      }, 500)
    }
  }

  // 컴포넌트 언마운트 시 타이머 정리
  useEffect(() => {
    return () => {
      if (searchTimer) {
        clearTimeout(searchTimer)
      }
    }
  }, [searchTimer])

  // 로딩 상태 렌더링
  if (loading) {
    return (
        <section className="pf-page">
          <div className="background">
            <div className="layout">
              <h1 className="page-title">교회 소식</h1>

              <div className="news-controls">
                <div className="search-container">
                  <div className="search-box">
                    <i className="fas fa-search search-icon"></i>
                    <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => handleSearchChange(e.target.value)}
                        placeholder="제목으로 검색..."
                        disabled
                    />
                    <button className="search-btn" disabled>
                      <i className="fas fa-search"></i>
                    </button>
                  </div>
                </div>
                {/* 관리자 권한이 있을 때만 글쓰기 버튼 표시 */}
                {hasAdminPermission() && (
                    <button className="write-btn" disabled>
                      <i className="fas fa-pen"></i> 글쓰기
                    </button>
                )}
              </div>

              <BoardLoading
                  title={isSearching ? "검색 중..." : "교회 소식을 불러오는 중..."}
                  subtitle="잠시만 기다려주세요"
                  size="medium"
              />
            </div>
          </div>
        </section>
    )
  }

  // 에러 상태 렌더링
  if (error) {
    return (
        <section className="pf-page">
          <div className="background">
            <div className="layout">
              <div className="error-container" style={{ textAlign: "center", padding: "50px" }}>
                <p style={{ color: "red", fontSize: "18px", marginBottom: "20px" }}>오류: {error}</p>
                <button
                    onClick={() => loadNews(currentPage, searchTerm)}
                    style={{
                      padding: "12px 24px",
                      background: "#003478",
                      color: "white",
                      border: "none",
                      borderRadius: "8px",
                      cursor: "pointer",
                    }}
                >
                  다시 시도
                </button>
              </div>
            </div>
          </div>
        </section>
    )
  }

  // 빈 상태 체크
  const isEmpty = !news || news.length === 0
  const isSearchEmpty = isEmpty && searchTerm.trim() !== ""

  return (
      <section className="pf-page">
        <div className="background">
          <div className="layout">
            <h1 className="page-title">교회 소식</h1>

            <div className="news-controls">
              <div className="search-container">
                <div className="search-box">
                  <i className="fas fa-search search-icon"></i>
                  <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => handleSearchChange(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === "Enter") {
                          handleSearch()
                        }
                      }}
                      placeholder="제목으로 검색..."
                  />
                  {searchTerm && (
                      <button className="clear-search-btn" onClick={handleClearSearch} title="검색어 지우기">
                        <i className="fas fa-times"></i>
                      </button>
                  )}
                  <button className="search-btn" onClick={handleSearch} title="검색">
                    <i className="fas fa-search"></i>
                  </button>
                </div>
              </div>
              {/* 관리자 권한이 있을 때만 글쓰기 버튼 표시 */}
              {hasAdminPermission() && (
                  <button className="write-btn" onClick={handleWriteClick}>
                    <i className="fas fa-pen"></i> 글쓰기
                  </button>
              )}
            </div>

            {/* 검색 결과 정보 */}
            {isSearching && (
                <div className="search-info">
                  <p>
                    <strong>"{searchTerm}"</strong> 검색 결과: {totalElements}개
                    <button onClick={handleClearSearch} className="clear-search-link">
                      검색 초기화
                    </button>
                  </p>
                </div>
            )}

            {/* 빈 상태 처리 */}
            {isEmpty ? (
                <EmptyState
                    type={isSearchEmpty ? "no-search-results" : "no-posts"}
                    searchTerm={searchTerm}
                    onClearSearch={handleClearSearch}
                    onWritePost={hasAdminPermission() ? handleWriteClick : null} // 권한이 있을 때만 글쓰기 함수 전달
                />
            ) : (
                <>
                  <PC>
                    <div className="news-table-container">
                      <table className="news-table">
                        <thead>
                        <tr>
                          <th className="col-num">번호</th>
                          <th className="col-category">분류</th>
                          <th className="col-title">제목</th>
                          <th className="col-author">작성자</th>
                          <th className="col-date">날짜</th>
                          <th className="col-views">조회수</th>
                          {hasAdminPermission() && <th className="col-actions">관리</th>}
                        </tr>
                        </thead>
                        <tbody>
                        {news.map((item, index) => {
                          const postId = getPostId(item)
                          return (
                              <tr key={index} className="news-row">
                                <td className="col-num">{getDisplayNumber(item, index)}</td>
                                <td className="col-category">
                                  <span className="category-badge">{item.category || "공지사항"}</span>
                                </td>
                                <td className="col-title">
                                  <a
                                      href="#"
                                      onClick={(e) => {
                                        e.preventDefault()
                                        handlePostClick(item, index)
                                      }}
                                      className="title-link"
                                  >
                                    {item.title}
                                  </a>
                                </td>
                                <td className="col-author">{item.author || item.writer || item.memberName}</td>
                                <td className="col-date">{item.createdDate || item.date || item.createdAt}</td>
                                <td className="col-views">{item.views || item.viewCount || 0}</td>
                                {hasAdminPermission() && (
                                    <td className="col-actions">
                                      <div className="action-dropdown">
                                        <button
                                            className="action-btn"
                                            onClick={() => toggleDropdown(postId)}
                                            title="관리 메뉴"
                                        >
                                          <i className="fas fa-ellipsis-v"></i>
                                        </button>
                                        {showDropdown[postId] && (
                                            <div className="dropdown-menu">
                                              <button onClick={(e) => handleEditClick(item, e)} className="dropdown-item edit">
                                                <i className="fas fa-edit"></i> 수정
                                              </button>
                                              <button
                                                  onClick={(e) => handleDeleteClick(item, e)}
                                                  className="dropdown-item delete"
                                              >
                                                <i className="fas fa-trash"></i> 삭제
                                              </button>
                                            </div>
                                        )}
                                      </div>
                                    </td>
                                )}
                              </tr>
                          )
                        })}
                        </tbody>
                      </table>
                    </div>
                  </PC>

                  <Notebook>
                    <div className="news-table-container">
                      <table className="news-table notebook">
                        <thead>
                        <tr>
                          <th className="col-num">번호</th>
                          <th className="col-category">분류</th>
                          <th className="col-title">제목</th>
                          <th className="col-author">작성자</th>
                          <th className="col-date">날짜</th>
                          <th className="col-views">조회수</th>
                          {hasAdminPermission() && <th className="col-actions">관리</th>}
                        </tr>
                        </thead>
                        <tbody>
                        {news.map((item, index) => {
                          const postId = getPostId(item)
                          return (
                              <tr key={index} className="news-row">
                                <td className="col-num">{getDisplayNumber(item, index)}</td>
                                <td className="col-category">
                                  <span className="category-badge">{item.category || "공지사항"}</span>
                                </td>
                                <td className="col-title">
                                  <a
                                      href="#"
                                      onClick={(e) => {
                                        e.preventDefault()
                                        handlePostClick(item, index)
                                      }}
                                      className="title-link"
                                  >
                                    {item.title}
                                  </a>
                                </td>
                                <td className="col-author">{item.author || item.writer || item.memberName}</td>
                                <td className="col-date">{item.createdDate || item.date || item.createdAt}</td>
                                <td className="col-views">{item.views || item.viewCount || 0}</td>
                                {hasAdminPermission() && (
                                    <td className="col-actions">
                                      <div className="action-dropdown">
                                        <button
                                            className="action-btn"
                                            onClick={() => toggleDropdown(postId)}
                                            title="관리 메뉴"
                                        >
                                          <i className="fas fa-ellipsis-v"></i>
                                        </button>
                                        {showDropdown[postId] && (
                                            <div className="dropdown-menu">
                                              <button onClick={(e) => handleEditClick(item, e)} className="dropdown-item edit">
                                                <i className="fas fa-edit"></i> 수정
                                              </button>
                                              <button
                                                  onClick={(e) => handleDeleteClick(item, e)}
                                                  className="dropdown-item delete"
                                              >
                                                <i className="fas fa-trash"></i> 삭제
                                              </button>
                                            </div>
                                        )}
                                      </div>
                                    </td>
                                )}
                              </tr>
                          )
                        })}
                        </tbody>
                      </table>
                    </div>
                  </Notebook>

                  <Tablet>
                    <div className="news-table-container">
                      <table className="news-table tablet">
                        <thead>
                        <tr>
                          <th className="col-num">번호</th>
                          <th className="col-title">제목</th>
                          <th className="col-date">날짜</th>
                          {hasAdminPermission() && <th className="col-actions">관리</th>}
                        </tr>
                        </thead>
                        <tbody>
                        {news.map((item, index) => {
                          const postId = getPostId(item)
                          return (
                              <tr key={index} className="news-row">
                                <td className="col-num">{getDisplayNumber(item, index)}</td>
                                <td className="col-title">
                                  <div className="title-content">
                                    <span className="category-badge">{item.category || "공지사항"}</span>
                                    <a
                                        href="#"
                                        onClick={(e) => {
                                          e.preventDefault()
                                          handlePostClick(item, index)
                                        }}
                                        className="title-link"
                                    >
                                      {item.title}
                                    </a>
                                    <div className="meta-info">
                                      <span>작성자: {item.author || item.writer || item.memberName}</span>
                                      <span>조회수: {item.views || item.viewCount || 0}</span>
                                    </div>
                                  </div>
                                </td>
                                <td className="col-date">{item.createdDate || item.date || item.createdAt}</td>
                                {hasAdminPermission() && (
                                    <td className="col-actions">
                                      <div className="action-dropdown">
                                        <button
                                            className="action-btn"
                                            onClick={() => toggleDropdown(postId)}
                                            title="관리 메뉴"
                                        >
                                          <i className="fas fa-ellipsis-v"></i>
                                        </button>
                                        {showDropdown[postId] && (
                                            <div className="dropdown-menu">
                                              <button onClick={(e) => handleEditClick(item, e)} className="dropdown-item edit">
                                                <i className="fas fa-edit"></i> 수정
                                              </button>
                                              <button
                                                  onClick={(e) => handleDeleteClick(item, e)}
                                                  className="dropdown-item delete"
                                              >
                                                <i className="fas fa-trash"></i> 삭제
                                              </button>
                                            </div>
                                        )}
                                      </div>
                                    </td>
                                )}
                              </tr>
                          )
                        })}
                        </tbody>
                      </table>
                    </div>
                  </Tablet>

                  <Mobile>
                    <div className="mobile-news-list">
                      {news.map((item, index) => {
                        const postId = getPostId(item)
                        return (
                            <div key={index} className="mobile-news-item">
                              <div className="mobile-header">
                                <span className="category-badge">{item.category || "공지사항"}</span>
                                <div className="mobile-actions">
                                  <span className="news-number">No. {getDisplayNumber(item, index)}</span>
                                  {hasAdminPermission() && (
                                      <div className="action-dropdown">
                                        <button className="action-btn" onClick={() => toggleDropdown(postId)} title="관리 메뉴">
                                          <i className="fas fa-ellipsis-v"></i>
                                        </button>
                                        {showDropdown[postId] && (
                                            <div className="dropdown-menu">
                                              <button onClick={(e) => handleEditClick(item, e)} className="dropdown-item edit">
                                                <i className="fas fa-edit"></i> 수정
                                              </button>
                                              <button
                                                  onClick={(e) => handleDeleteClick(item, e)}
                                                  className="dropdown-item delete"
                                              >
                                                <i className="fas fa-trash"></i> 삭제
                                              </button>
                                            </div>
                                        )}
                                      </div>
                                  )}
                                </div>
                              </div>
                              <h3 className="mobile-title" onClick={() => handlePostClick(item, index)}>
                                {item.title}
                              </h3>
                              <div className="mobile-meta">
                          <span>
                            <i className="far fa-user"></i> {item.author || item.writer || item.memberName}
                          </span>
                                <span>
                            <i className="far fa-calendar"></i> {item.createdDate || item.date || item.createdAt}
                          </span>
                                <span>
                            <i className="far fa-eye"></i> {item.views || item.viewCount || 0}
                          </span>
                              </div>
                            </div>
                        )
                      })}
                    </div>
                  </Mobile>

                  <div className="pagination">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((number) => (
                        <button
                            key={number}
                            onClick={() => paginate(number)}
                            className={currentPage === number ? "active" : ""}
                        >
                          {number}
                        </button>
                    ))}
                  </div>
                </>
            )}
          </div>
        </div>

        <style jsx>{`
        .action-dropdown {
          position: relative;
          display: inline-block;
        }

        .action-btn {
          background: none;
          border: none;
          cursor: pointer;
          padding: 8px;
          border-radius: 4px;
          color: #666;
          transition: background-color 0.2s;
        }

        .action-btn:hover {
          background-color: #f5f5f5;
        }

        .dropdown-menu {
          position: absolute;
          top: 100%;
          right: 0;
          background: white;
          border: 1px solid #ddd;
          border-radius: 8px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          z-index: 1000;
          min-width: 120px;
        }

        .dropdown-item {
          display: flex;
          align-items: center;
          gap: 8px;
          width: 100%;
          padding: 12px 16px;
          background: none;
          border: none;
          text-align: left;
          cursor: pointer;
          font-size: 14px;
          color: #333;
          transition: background-color 0.2s;
        }

        .dropdown-item:hover {
          background-color: #f8f9fa;
        }

        .dropdown-item.edit {
          color: #007bff;
        }

        .dropdown-item.delete {
          color: #dc3545;
        }

        .dropdown-item.delete:hover {
          background-color: #fff5f5;
        }

        .col-actions {
          width: 60px;
          text-align: center;
        }

        .mobile-actions {
          display: flex;
          align-items: center;
          gap: 10px;
        }

        @media (max-width: 768px) {
          .dropdown-menu {
            right: -10px;
          }
        }
      `}</style>
      </section>
  )
}

export default ChurchNews
